/* The printed breakdown.
 *
 * One model, two outputs. Every receipt is built as a list of fixed-width
 * monospace lines; the screen renders them in a <pre> and the PDF draws the
 * same lines in Courier. There is no second layout to keep in sync, so what a
 * manager sees on the print preview is what lands on the paper and in the file.
 *
 * 68 columns at 10pt Courier is 408pt of the 612pt page, centered. About 54
 * lines fit per page, so a pool split stays on one page through roughly 30
 * people and rolls onto a clean second page after that.
 */

import { formatMoney, formatHundredths } from "../../lib/formulas/tipSplit";

export const RECEIPT_WIDTH = 68;

const L = (text = "", bold = false) => ({ text, bold });

const clip = (text, len) => {
  const s = String(text ?? "");
  return s.length <= len ? s : `${s.slice(0, Math.max(0, len - 1))}.`;
};
const pad = (text, len) => clip(text, len).padEnd(len);
const padLeft = (text, len) => clip(text, len).padStart(len);
const ruleOf = (char) => char.repeat(RECEIPT_WIDTH);

const center = (text) => {
  const s = clip(text, RECEIPT_WIDTH);
  const left = Math.floor((RECEIPT_WIDTH - s.length) / 2);
  return " ".repeat(Math.max(0, left)) + s;
};

// Label on the left, number hard against the right edge, like a register slip.
const row = (label, value) => {
  const v = String(value);
  return pad(label, Math.max(0, RECEIPT_WIDTH - v.length)) + v;
};

const wrap = (text) => {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    if (!current) current = word;
    else if (current.length + 1 + word.length <= RECEIPT_WIDTH) current += ` ${word}`;
    else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  return lines;
};

const rate6 = (rate) => `$${rate.toFixed(6)}`;

// The word a deduction line uses for what it was a percentage of.
const basisWord = (t, basis) => {
  if (basis === "sales") return t.rules.basisSales;
  if (basis === "food") return t.rules.basisFood;
  if (basis === "liquor") return t.rules.basisLiquor;
  return t.rules.basisTips;
};

function headerLines({ businessName, shiftLabel, title }) {
  const out = [L(ruleOf("="))];
  if (businessName) out.push(L(center(businessName.toUpperCase()), true));
  out.push(L(center(title), true));
  if (shiftLabel) out.push(L(center(shiftLabel)));
  out.push(L(ruleOf("=")));
  return out;
}

function footerLines(t) {
  return [
    L(),
    ...wrap(t.receipt.footnote).map((line) => L(line)),
    L(),
    ...wrap(t.disclaimer).map((line) => L(line)),
  ];
}

/* ── Pool and split ──────────────────────────────────────────────────────── */

const poolRow = (name, role, hours, pts, weighted, share, joined = true) =>
  pad(name, 20) +
  " " +
  pad(role, 10) +
  " " +
  padLeft(hours, 6) +
  (joined ? " x " : "   ") +
  padLeft(pts, 5) +
  (joined ? " = " : "   ") +
  padLeft(weighted, 7) +
  padLeft(share, 12);

export function buildPoolReceipt({ t, businessName, shiftLabel, result }) {
  const lines = headerLines({
    businessName,
    shiftLabel,
    title: t.receipt.poolTitle,
  });

  lines.push(
    L(row(t.receipt.pool, formatMoney(result.poolCents))),
    L(row(t.receipt.totalWeighted, formatHundredths(result.totalWeightedHoursCenti))),
    L(row(t.receipt.rate, rate6(result.ratePerWeightedHour))),
    L(ruleOf("-")),
    L(
      poolRow(
        t.receipt.colName,
        t.receipt.colRole,
        t.receipt.colHours,
        t.receipt.colPoints,
        t.receipt.colWeighted,
        t.receipt.colShare,
        false
      )
    ),
    L(ruleOf("-"))
  );

  result.rows.forEach((r) => {
    lines.push(
      L(
        poolRow(
          r.name,
          r.role,
          formatHundredths(r.hoursCenti),
          formatHundredths(r.pointsCenti),
          formatHundredths(r.weightedHoursCenti),
          formatMoney(r.shareCents)
        )
      )
    );
  });

  lines.push(
    L(ruleOf("-")),
    L(
      poolRow(
        t.receipt.total,
        "",
        "",
        "",
        formatHundredths(result.totalWeightedHoursCenti),
        formatMoney(result.poolCents),
        false
      ),
      true
    ),
    L(ruleOf("=")),
    L(t.receipt.shareFormula(rate6(result.ratePerWeightedHour))),
    ...footerLines(t)
  );

  return lines;
}

/* ── Tip-out ─────────────────────────────────────────────────────────────── */

const supportRow = (name, hours, pts, weighted, share, joined = true) =>
  "  " +
  pad(name, 20) +
  padLeft(hours, 6) +
  (joined ? " x " : "   ") +
  padLeft(pts, 5) +
  (joined ? " = " : "   ") +
  padLeft(weighted, 8) +
  padLeft(share, 12);

export function buildTipOutReceipt({ t, businessName, shiftLabel, result }) {
  const lines = headerLines({
    businessName,
    shiftLabel,
    title: t.receipt.tipoutTitle,
  });

  lines.push(L(t.receipt.contributors, true), L(ruleOf("-")));

  result.contributors.forEach((c, i) => {
    if (i > 0) lines.push(L());
    lines.push(L(`${c.name} (${c.roleName})`));
    lines.push(L(row(`  ${t.receipt.tips}`, formatMoney(c.tipsCents))));
    c.deductions.forEach((d) => {
      const label = t.receipt.ruleLine(
        formatHundredths(d.pctCenti),
        basisWord(t, d.basis),
        d.toRoleName
      );
      lines.push(L(row(`  ${label}`, `-${formatMoney(d.amountCents)}`)));
    });
    lines.push(L(row(`  ${t.receipt.takeHome}`, formatMoney(c.takeHomeCents)), true));
  });

  if (result.pools.length) {
    lines.push(L(), L(t.receipt.support, true), L(ruleOf("-")));
    result.pools.forEach((pool, i) => {
      if (i > 0) lines.push(L());
      lines.push(L(t.receipt.poolFor(pool.roleName, formatMoney(pool.poolCents))));
      lines.push(
        L(
          supportRow(
            t.receipt.colName,
            t.receipt.colHours,
            t.receipt.colPoints,
            t.receipt.colWeighted,
            t.receipt.colShare,
            false
          )
        )
      );
      pool.recipients.forEach((r) => {
        lines.push(
          L(
            supportRow(
              r.name,
              formatHundredths(r.hoursCenti),
              formatHundredths(r.pointsCenti),
              formatHundredths(r.weightedHoursCenti),
              formatMoney(r.shareCents)
            )
          )
        );
      });
    });
  }

  lines.push(
    L(ruleOf("=")),
    ...wrap(
      t.receipt.conservation(
        formatMoney(result.totalTipsCents),
        formatMoney(result.totalTakeHomeCents),
        formatMoney(result.totalSharesCents)
      )
    ).map((line) => L(line, true)),
    ...footerLines(t)
  );

  return lines;
}

/* ── Blank sign sheet ────────────────────────────────────────────────────── */

const signRow = (name, role, share, signature) =>
  pad(name, 20) + " " + pad(role, 12) + " " + padLeft(share, 10) + "  " + signature;

/**
 * People sign next to their share. When a valid split exists the share is
 * printed; otherwise it is a blank for the manager to fill in by hand.
 */
export function buildSignSheet({ t, businessName, shiftLabel, people }) {
  const lines = headerLines({
    businessName,
    shiftLabel,
    title: t.receipt.blankTitle,
  });

  lines.push(
    L(signRow(t.receipt.colName, t.receipt.colRole, t.receipt.colShare, t.receipt.colSignature)),
    L(ruleOf("-"))
  );

  people.forEach((p) => {
    lines.push(
      L(
        signRow(
          p.name,
          p.role,
          p.shareCents === null || p.shareCents === undefined
            ? "________"
            : formatMoney(p.shareCents),
          "_".repeat(21)
        )
      ),
      L()
    );
  });

  lines.push(L(ruleOf("=")), ...footerLines(t));
  return lines;
}

/* ── PDF ─────────────────────────────────────────────────────────────────── */

// pdf-lib's standard fonts encode WinAnsi. Map the punctuation a phone keyboard
// inserts on its own, then drop anything still outside the range so a name with
// an unusual character prints as a name instead of throwing mid-download.
const SMART_QUOTES = /[‘’‛]/g;
const SMART_DOUBLES = /[“”]/g;
const DASHES = /[–—]/g;
const ELLIPSIS = /…/g;
const OUTSIDE_WINANSI = /[^\x20-\x7E\u00A0-\u00FF]/g;

const sanitize = (text) =>
  String(text)
    .replace(SMART_QUOTES, "'")
    .replace(SMART_DOUBLES, '"')
    .replace(DASHES, "-")
    .replace(ELLIPSIS, "...")
    .replace(OUTSIDE_WINANSI, "?");

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Draw the receipt lines to a letter-size PDF and download it. pdf-lib is
 * dynamically imported so it stays out of every other page's bundle, the same
 * way the QR tool loads its renderer.
 */
export async function downloadReceiptPdf(lines, filename) {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");

  const PAGE_W = 612; // 8.5in at 72dpi
  const PAGE_H = 792; // 11in
  const SIZE = 10;
  const LEADING = 12.6;
  const MARGIN_Y = 54;
  const CHAR_W = SIZE * 0.6; // Courier is exactly 600/1000 em
  const left = (PAGE_W - RECEIPT_WIDTH * CHAR_W) / 2;

  const doc = await PDFDocument.create();
  const regular = await doc.embedFont(StandardFonts.Courier);
  const bold = await doc.embedFont(StandardFonts.CourierBold);
  const black = rgb(0, 0, 0);

  let page = null;
  let y = 0;
  const nextPage = () => {
    page = doc.addPage([PAGE_W, PAGE_H]);
    y = PAGE_H - MARGIN_Y;
  };
  nextPage();

  lines.forEach((line) => {
    if (y < MARGIN_Y) nextPage();
    const text = sanitize(line.text || "");
    if (text.trim()) {
      page.drawText(text, {
        x: left,
        y,
        size: SIZE,
        font: line.bold ? bold : regular,
        color: black,
      });
    }
    y -= LEADING;
  });

  const bytes = await doc.save();
  saveBlob(new Blob([bytes], { type: "application/pdf" }), filename);
}
